import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { useToast } from '../../../hooks/use-toast';
import { Loader2, Save, Plus, Trash2, Upload, Image as ImageIcon, GripVertical } from 'lucide-react';
import BackButton from '../../../components/admin/BackButton';
import TranslateButton from '../../../components/admin/TranslateButton';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BLANK_LOGO = { name: '', logo_base64: '', logo_url: '', website_url: '', order: 0 };

const ClientsForm = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const fileInputsRef = useRef({});

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    heading_pt: 'Confiados por Organizações Líderes',
    heading_en: 'Trusted by Leading Organizations',
    subheading_pt: 'Orgulhamo-nos de trabalhar com empresas que partilham o nosso compromisso com a excelência.',
    subheading_en: "We're proud to work with companies who share our commitment to excellence.",
    logos: [],
  });

  useEffect(() => { loadClientsData(); }, []);

  const loadClientsData = async () => {
    setLoading(true);
    try {
      const [ptResponse, enResponse, rawResponse] = await Promise.all([
        axios.get(`${API_URL}/api/homepage-ml/clients?lang=pt`),
        axios.get(`${API_URL}/api/homepage-ml/clients?lang=en`),
        axios.get(`${API_URL}/api/homepage-ml/clients?lang=raw`),
      ]);

      setFormData({
        heading_pt: ptResponse.data.heading || '',
        heading_en: enResponse.data.heading || '',
        subheading_pt: ptResponse.data.subheading || '',
        subheading_en: enResponse.data.subheading || '',
        logos: Array.isArray(rawResponse.data.logos) ? rawResponse.data.logos : [],
      });
    } catch (error) {
      console.error('Failed to load clients:', error);
    }
    setLoading(false);
  };

  const handleInputChange = (field, value) => setFormData((p) => ({ ...p, [field]: value }));

  const addLogo = () => setFormData((p) => ({
    ...p,
    logos: [...p.logos, { ...BLANK_LOGO, order: p.logos.length }],
  }));

  const removeLogo = (idx) => setFormData((p) => ({
    ...p, logos: p.logos.filter((_, i) => i !== idx),
  }));

  const updateLogo = (idx, key, value) => setFormData((p) => {
    const next = [...p.logos];
    next[idx] = { ...next[idx], [key]: value };
    return { ...p, logos: next };
  });

  const moveLogo = (idx, dir) => setFormData((p) => {
    const next = [...p.logos];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return p;
    [next[idx], next[target]] = [next[target], next[idx]];
    return { ...p, logos: next.map((l, i) => ({ ...l, order: i })) };
  });

  const handleFileUpload = async (e, idx) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast({ variant: 'destructive', title: 'Ficheiro inválido', description: 'Selecione uma imagem.' });
      return;
    }
    if (file.size > 500_000) {
      toast({ variant: 'destructive', title: 'Imagem muito grande', description: 'Máx. 500KB. Reduza o tamanho do logo.' });
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      updateLogo(idx, 'logo_base64', ev.target.result);
      toast({ title: 'Logo carregado', description: file.name });
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...formData,
        logos: formData.logos.map((l, i) => ({ ...l, order: i })),
      };
      await axios.put(`${API_URL}/api/homepage-ml/clients`, payload);
      toast({ title: 'Sucesso', description: 'Secção Clients guardada' });
    } catch (error) {
      toast({ variant: 'destructive', title: 'Erro', description: 'Falha ao guardar' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#00B4D8]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-6 max-w-5xl">
        <BackButton />

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Secção Clients — Trusted by Leading Organizations</CardTitle>
            <CardDescription>Configure títulos, subtítulos e logos dos clientes/parceiros.</CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Portuguese */}
              <div className="border-b pb-6">
                <h3 className="text-lg font-semibold mb-4 text-[#00B4D8]">🇵🇹 Português</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Título (PT)</label>
                    <input type="text" value={formData.heading_pt}
                      onChange={(e) => handleInputChange('heading_pt', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg" data-testid="heading-pt" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Subtítulo (PT)</label>
                    <textarea value={formData.subheading_pt}
                      onChange={(e) => handleInputChange('subheading_pt', e.target.value)}
                      rows={2} className="w-full px-4 py-2 border rounded-lg" data-testid="subheading-pt" />
                  </div>
                </div>
              </div>

              {/* English */}
              <div className="border-b pb-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-[#00B4D8]">🇬🇧 English</h3>
                  <TranslateButton
                    sourceTexts={{ heading: formData.heading_pt, subheading: formData.subheading_pt }}
                    onTranslated={(t) => setFormData((p) => ({
                      ...p, heading_en: t.heading || '', subheading_en: t.subheading || '',
                    }))}
                  />
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Heading (EN)</label>
                    <input type="text" value={formData.heading_en}
                      onChange={(e) => handleInputChange('heading_en', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg" data-testid="heading-en" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Subheading (EN)</label>
                    <textarea value={formData.subheading_en}
                      onChange={(e) => handleInputChange('subheading_en', e.target.value)}
                      rows={2} className="w-full px-4 py-2 border rounded-lg" data-testid="subheading-en" />
                  </div>
                </div>
              </div>

              {/* Logos manager */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-[#00B4D8] flex items-center gap-2">
                    <ImageIcon className="w-5 h-5" /> Logos de Clientes ({formData.logos.length})
                  </h3>
                  <Button type="button" onClick={addLogo} className="bg-[#00B4D8] hover:bg-[#0088B0]" data-testid="add-logo-btn">
                    <Plus className="w-4 h-4 mr-1" /> Adicionar Logo
                  </Button>
                </div>

                {formData.logos.length === 0 && (
                  <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg text-gray-500">
                    Nenhum logo adicionado ainda. Clique em "Adicionar Logo" para começar.
                  </div>
                )}

                <div className="space-y-3">
                  {formData.logos.map((logo, idx) => (
                    <div key={idx} className="border rounded-lg p-4 bg-white grid grid-cols-1 md:grid-cols-12 gap-3 items-start" data-testid={`logo-row-${idx}`}>
                      {/* Order controls */}
                      <div className="md:col-span-1 flex md:flex-col items-center justify-center gap-1 text-gray-400">
                        <GripVertical className="w-4 h-4" />
                        <div className="flex md:flex-col gap-1">
                          <button type="button" disabled={idx === 0}
                            onClick={() => moveLogo(idx, -1)}
                            className="px-2 text-xs hover:text-gray-900 disabled:opacity-30">▲</button>
                          <button type="button" disabled={idx === formData.logos.length - 1}
                            onClick={() => moveLogo(idx, 1)}
                            className="px-2 text-xs hover:text-gray-900 disabled:opacity-30">▼</button>
                        </div>
                      </div>

                      {/* Preview */}
                      <div className="md:col-span-2 flex justify-center">
                        <div className="w-20 h-20 border rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden">
                          {logo.logo_base64 || logo.logo_url ? (
                            <img src={logo.logo_base64 || logo.logo_url} alt={logo.name}
                              className="max-w-full max-h-full object-contain" />
                          ) : (
                            <ImageIcon className="w-6 h-6 text-gray-300" />
                          )}
                        </div>
                      </div>

                      {/* Fields */}
                      <div className="md:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Nome da Empresa *</label>
                          <input type="text" value={logo.name}
                            onChange={(e) => updateLogo(idx, 'name', e.target.value)}
                            placeholder="Ex: Emirates NBD"
                            className="w-full px-3 py-1.5 border rounded text-sm"
                            data-testid={`logo-name-${idx}`} />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">URL do site (opcional)</label>
                          <input type="url" value={logo.website_url}
                            onChange={(e) => updateLogo(idx, 'website_url', e.target.value)}
                            placeholder="https://…"
                            className="w-full px-3 py-1.5 border rounded text-sm" />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-xs text-gray-600 mb-1">Logo URL (alternativa ao upload)</label>
                          <input type="url" value={logo.logo_url}
                            onChange={(e) => updateLogo(idx, 'logo_url', e.target.value)}
                            placeholder="https://exemplo.com/logo.png"
                            className="w-full px-3 py-1.5 border rounded text-sm" />
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="md:col-span-1 flex md:flex-col gap-2 justify-end">
                        <input
                          type="file" accept="image/*" hidden
                          ref={(el) => (fileInputsRef.current[idx] = el)}
                          onChange={(e) => handleFileUpload(e, idx)}
                        />
                        <Button type="button" size="sm" variant="outline"
                          onClick={() => fileInputsRef.current[idx]?.click()}
                          data-testid={`upload-logo-${idx}`}
                          title="Carregar ficheiro (máx 500KB)"
                        >
                          <Upload className="w-3 h-3" />
                        </Button>
                        <Button type="button" size="sm" variant="outline"
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => removeLogo(idx)}
                          data-testid={`remove-logo-${idx}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 bg-blue-50 p-3 rounded-lg text-xs text-blue-900">
                  <strong>Dica:</strong> Logos monocromáticos em PNG transparente funcionam melhor (grayscale é aplicado na homepage e fica colorido no hover). Tamanho recomendado: 200x80px.
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-6 border-t">
                <Button type="button" variant="outline" onClick={() => navigate('/admin/homepage')}>Cancelar</Button>
                <Button type="submit" disabled={saving} className="bg-[#00B4D8]" data-testid="save-btn">
                  {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />A guardar...</> : <><Save className="w-4 h-4 mr-2" />Guardar</>}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ClientsForm;
